import * as R from "ramda";
import { stringify } from "query-string";
import {
  fetchUtils,
  GET_LIST,
  GET_ONE,
  GET_MANY,
  GET_MANY_REFERENCE,
  CREATE,
  UPDATE,
  UPDATE_MANY,
  DELETE,
  DELETE_MANY,
} from "react-admin";
import {
  ENABLE_2FA,
  VALIDATE_QR,
  DISABLE_2FA,
  STATUS_2FA,
  CHANGE_PASSWORD,
  SEND_PUSH,
  GET_REMOTE_SESSION,
  SET_REMOTE_SESSION,
  SEND_CLINIC_INVITATION_EMAIL,
} from "./constants";
import { USER_LOAD_START } from "../redux/user/actions";
import {
  UPLOAD_CSV_TO_STATIONS,
  UPLOAD_STATION_DEVICES,
} from "../redux/stations/actions";
import {
  PATIENT_INFO_LOAD_START,
  LINK_TO_CONSUMER,
  UNLINK_FROM_CONSUMER,
} from "../redux/patients/actions";

/**
 * Maps react-admin queries to a simple REST API
 *
 * The REST dialect is similar to the one of FakeRest
 * @see https://github.com/marmelab/FakeRest
 * @example
 * GET_LIST     => GET http://my.api.url/posts?sort=['title','ASC']&range=[0, 24]
 * GET_ONE      => GET http://my.api.url/posts/123
 * GET_MANY     => GET http://my.api.url/posts?filter={ids:[123,456,789]}
 * UPDATE       => PUT http://my.api.url/posts/123
 * CREATE       => POST http://my.api.url/posts/123
 * DELETE       => DELETE http://my.api.url/posts/123
 */
export default (apiUrl, httpClient = fetchUtils.fetchJson) => {
  /**
   * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
   * @param {String} resource Name of the resource to fetch, e.g. 'posts'
   * @param {Object} params The data request params, depending on the type
   * @returns {Object} { url, options } The HTTP request parameters
   */
  const convertDataRequestToHTTP = (type, resource, params) => {
    let url = "";
    const options = {};
    switch (type) {
      case GET_LIST: {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const { q, ...restFilter } = params.filter;
        const query = {
          // sort: JSON.stringify([field, order]),
          ordering: `${order === "ASC" ? "" : "-"}${field}`,
          page_size: perPage,
          page,
          search: R.pathOr(undefined, ["filter", "q"], params),
          ...restFilter,
          // range: JSON.stringify([
          //     (page - 1) * perPage,
          //     page * perPage - 1,
          // ]),
        };
        url = `${apiUrl}/${resource}/?${stringify(query)}`;
        break;
      }
      case GET_ONE:
        url = `${apiUrl}/${resource}/${params.id}/`;
        break;
      case GET_MANY: {
        const query = {
          ids: params.ids.join(","),
        };
        url = `${apiUrl}/${resource}/?${stringify(query)}`;
        break;
      }
      case GET_MANY_REFERENCE: {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const target = R.pathOr(undefined, ["target"], params);
        const id = R.pathOr(undefined, ["id"], params);
        const query = {
          ordering: `${order === "ASC" ? "" : "-"}${field}`,
          page_size: perPage,
          page,
          [target]: id,
        };

        url = `${apiUrl}/${resource}/?${stringify(query)}`;
        break;
      }
      case UPDATE:
        url = `${apiUrl}/${resource}/${params.id}/`;
        options.method = "PUT";
        options.body = JSON.stringify(params.data);
        if (resource === "staff") {
          // remove permission -> client will get new one - maybe it updated
          setTimeout(() => {
            localStorage.removeItem("permissions");
          }, 1500);
        } else if (resource === "station_activations") {
          url = `${apiUrl}/${resource}/${params.id}/deactivate/`;
          options.method = "POST";
          options.body = JSON.stringify(params.data);
        }

        break;
      case CREATE:
        const { data } = params;
        url = `${apiUrl}/${resource}/`;
        options.method = "POST";
        options.body = JSON.stringify(data);
        break;
      case DELETE:
        const { checked } = params;
        if (checked === true) {
          url = `${apiUrl}/${resource}/${params.id}/?removeFromConsumer=1`;
        } else {
          url = `${apiUrl}/${resource}/${params.id}/`;
        }
        options.method = "DELETE";
        break;
      case ENABLE_2FA:
        url = `${apiUrl}/tfa/qr/enable`;
        options.method = "POST";
        break;
      case DISABLE_2FA:
        url = `${apiUrl}/tfa/disable`;
        options.method = "POST";
        break;
      case STATUS_2FA:
        url = `${apiUrl}/tfa/status`;
        options.method = "GET";
        break;
      case VALIDATE_QR:
        url = `${apiUrl}/tfa/qr/validate`;
        options.method = "POST";
        options.body = JSON.stringify(params);
        break;
      case CHANGE_PASSWORD:
        url = `${apiUrl}/change_password`;
        options.method = "POST";
        options.body = JSON.stringify(params);
        break;
      case SEND_PUSH:
        url = `${apiUrl}/patients/${params.id}/send_push/`;
        options.method = "POST";
        break;
      case SEND_CLINIC_INVITATION_EMAIL:
        url = `${apiUrl}/clinic_invites/${params.id}/send_email`;
        options.method = "POST";
        break;
      case GET_REMOTE_SESSION:
        url = `${apiUrl}/app_config`;
        options.method = "GET";
        break;
      case SET_REMOTE_SESSION:
        url = `${apiUrl}/app_config`;
        options.method = "PUT";
        options.body = JSON.stringify(params);
        break;
      case USER_LOAD_START:
        url = `${apiUrl}/user`;
        options.method = "GET";
        break;
      case UPLOAD_CSV_TO_STATIONS:
        url = `${apiUrl}/stations/import-csv/`;
        options.method = "POST";
        options.body = JSON.stringify(params);
        break;
      case UPLOAD_STATION_DEVICES:
        url = `${apiUrl}/${resource}/${params.id}/`;
        options.method = "GET";
        break;
      case PATIENT_INFO_LOAD_START:
        url = `${apiUrl}/${resource}/${params.patientId}/consumer_info/`;
        options.method = "GET";
        break;
      case LINK_TO_CONSUMER:
        url = `${apiUrl}/${resource}/${params.id}/link_to_consumer/`;
        options.method = "POST";
        break;
      case UNLINK_FROM_CONSUMER:
        url = `${apiUrl}/${resource}/${params.id}/unlink_from_consumer/`;
        options.method = "POST";
        break;
      default:
        throw new Error(`Unsupported fetch action type ${type}`);
    }
    return { url, options };
  };

  /**
   * @param {Object} response HTTP response from fetch()
   * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
   * @param {String} resource Name of the resource to fetch, e.g. 'posts'
   * @param {Object} params The data request params, depending on the type
   * @returns {Object} Data response
   */
  const convertHTTPResponse = (response, type, resource, params) => {
    const { headers, json } = response;
    const { results, count } = json;
    switch (type) {
      case GET_LIST:
      case GET_MANY_REFERENCE:
        return {
          data: results,
          total: count,
        };
      case CREATE:
        return { data: { ...params.data, id: json.id } };
      default:
        return { data: json };
    }
  };

  /**
   * @param {string} type Request type, e.g GET_LIST
   * @param {string} resource Resource name, e.g. "posts"
   * @param {Object} payload Request parameters. Depends on the request type
   * @returns {Promise} the Promise for a data response
   */
  return (type, resource, params) => {
    // simple-rest doesn't handle filters on UPDATE route, so we fallback to calling UPDATE n times instead
    if (type === UPDATE_MANY) {
      return Promise.all(
        params.ids.map((id) =>
          httpClient(`${apiUrl}/${resource}/${id}/`, {
            method: "PUT",
            body: JSON.stringify(params.data),
          })
        )
      ).then((responses) => ({
        data: responses.map((response) => response.json),
      }));
    }
    // simple-rest doesn't handle filters on DELETE route, so we fallback to calling DELETE n times instead
    if (type === DELETE_MANY) {
      const { checked } = params;
      let getUrl = (id) => `${apiUrl}/${resource}/${id}/`;
      if (checked === true) {
        getUrl = (id) => `${apiUrl}/${resource}/${id}/?removeFromConsumer=1`;
      }
      return Promise.all(
        params.ids.map((id) =>
          httpClient(getUrl(id), {
            method: "DELETE",
          })
        )
      )
        .then((responses) => ({
          data: responses.map((response) => response.json),
        }))
        .catch((err) => {
          const message = R.pathOr(null, ["body", "message"], err);
          if (message) {
            throw new Error(message);
          }
        });
    }

    const { url, options } = convertDataRequestToHTTP(type, resource, params);
    return httpClient(url, options).then((response) =>
      convertHTTPResponse(response, type, resource, params)
    );
  };
};
